import { useModal } from "@flaminem/ui-components";
import useAxios from "axios-hooks";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Form } from "react-router-dom";

import { JSONEditor } from "src/components/JSONEditor";
import { ConfirmActionModal, ModalTransition } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { ConfirmPageReloadModal } from "src/features/schema/UpdateSchema/ConfirmPageReloadModal";
import { useT } from "src/internationalization";

import { CommentModal } from "./CommentModal";

type SchemaClientModalProp = {
  onClose: () => void;
  type: string;
  urlGet: string;
  urlUpdate: string;
};

export const UpdateSchemaModal: React.FC<SchemaClientModalProp> = ({
  onClose,
  type,
  urlGet,
  urlUpdate,
}) => {
  const { showSuccessToast } = useToast();
  const { isModalOpen, openModal, closeModal } = useModal();
  const mapToMutationError = useMapToMutationError();

  const [{ data: configuration, loading, error }] = useAxios(urlGet);
  const [{ loading: postLoading, error: postError }, executePost] = useAxios(
    {
      url: urlUpdate,
      method: "POST",
    },
    { manual: true }
  );
  const t = useT();
  const { handleSubmit, setError, control, setValue, clearErrors, watch } = useForm<{
    config: string;
  }>({
    mode: "onChange",
  });

  const currentConfig = watch("config");
  const originalConfig = configuration?.data
    ? JSON.stringify(JSON.parse(configuration.data), null, 2)
    : "";
  const isUnchanged = currentConfig === originalConfig;

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [configToSubmit, setConfigToSubmit] = useState<string | null>(null);

  const onSubmit = async (data) => {
    try {
      if (data.config) {
        // Validate JSON one more time before proceeding
        JSON.parse(data.config);
        setConfigToSubmit(data.config);
        setShowCommentModal(true);
      }
    } catch (e) {
      setError("config", { type: "invalid" });
    }
  };

  const handleCommentSubmit = async (comment: string) => {
    try {
      if (configToSubmit) {
        await executePost({
          data: {
            id: configuration.id,
            content: JSON.stringify(JSON.parse(configToSubmit), null, 2),
            comment,
          },
        });
        showSuccessToast(
          t("header.switcher.settings.schema.success.message", {
            type,
          })
        );
      }
      openModal();
    } finally {
      setShowCommentModal(false);
    }
  };

  useEffect(() => {
    if (configuration?.data) {
      try {
        setValue("config", JSON.stringify(JSON.parse(configuration.data), null, 2));
      } catch (e) {
        setValue("config", configuration.data, { shouldDirty: true });
      }
    }
  }, [configuration, setValue]);

  return (
    <>
      <ConfirmActionModal
        onClose={onClose}
        fullHeight
        size="lg"
        onConfirm={handleSubmit(onSubmit)}
        error={mapToModalError(mapToMutationError(error || postError))}
        loading={loading || postLoading}
        title={t("header.switcher.settings.schema.modal.title", {
          type,
        })}
        subtitle={t("header.switcher.settings.schema.modal.subtitle")}
        confirmButtonText={t("general.actions.confirm")}
        closeOnEscape={false}
        closeOnOutsideClick={false}
        confirmButtonDisabled={isUnchanged}
      >
        <Form noValidate={true} className="h-full">
          <Controller
            defaultValue={""}
            render={({ field, fieldState }) => (
              <JSONEditor
                label={t("header.switcher.settings.schema.modal.form.content.label")}
                value={field.value}
                onChange={field.onChange}
                onValidate={(markers) => {
                  clearErrors("config");
                  if (markers.length) {
                    setError("config", { type: "invalid" });
                  }
                }}
                invalid={fieldState.invalid}
                invalidText={
                  fieldState.error?.type === "required"
                    ? t("header.switcher.settings.schema.modal.form.content.required")
                    : t("header.switcher.settings.schema.modal.form.content.invalid")
                }
                helperText={t("header.switcher.settings.schema.modal.form.content.helper")}
              />
            )}
            name={"config"}
            control={control}
            rules={{ required: true }}
          />
        </Form>
      </ConfirmActionModal>
      <ModalTransition>
        {isModalOpen && (
          <ConfirmPageReloadModal
            onClose={() => {
              closeModal();
              onClose();
            }}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {showCommentModal && (
          <CommentModal
            onClose={() => setShowCommentModal(false)}
            onConfirm={handleCommentSubmit}
            loading={postLoading}
          />
        )}
      </ModalTransition>
    </>
  );
};
